import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Legacy key used by the old manual auth flow. The SDK now owns the session
// (persistence + automatic refresh token rotation). We migrate once, then
// delete this key so it can never feed stale tokens back into the SDK.
const LEGACY_KEY = 'supabase.auth.token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  const checkPremiumStatus = async (userId) => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from('premium_users')
        .select('*')
        .eq('user_id', userId);
      const hasPremium = !error && data && data.length > 0;
      setIsPremium(hasPremium);
    } catch (e) {
      console.error('Error checking premium:', e);
      setIsPremium(false);
    }
  };

  useEffect(() => {
    // Clean up legacy non-UUID user IDs from previous versions
    const legacyId = localStorage.getItem('bible-user-id');
    if (legacyId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(legacyId)) {
      localStorage.removeItem('bible-user-id');
    }

    let isMounted = true;

    // One-time migration: convert the legacy manually-stored tokens into an
    // official SDK session, then remove the legacy key. If the legacy refresh
    // token is already consumed/expired, the user simply has to log in again.
    const migrateLegacySession = async () => {
      const raw = localStorage.getItem(LEGACY_KEY);
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.access_token && parsed?.refresh_token) {
          const { data } = await supabase.auth.getSession();
          if (!data?.session) {
            const { error } = await supabase.auth.setSession({
              access_token: parsed.access_token,
              refresh_token: parsed.refresh_token,
            });
            if (error) {
              console.warn('Legacy session migration failed, re-login required:', error.message);
            }
          }
        }
      } catch (e) {
        console.warn('Legacy session migration error:', e?.message);
      } finally {
        localStorage.removeItem(LEGACY_KEY);
      }
    };

    const init = async () => {
      await migrateLegacySession();
      try {
        const { data } = await supabase.auth.getSession();
        if (!isMounted) return;
        if (data?.session?.user) {
          setUser(data.session.user);
          checkPremiumStatus(data.session.user.id);
        }
      } catch (e) {
        console.error('Error restoring session:', e);
      }
      if (isMounted) setLoading(false);
    };
    init();

    // Keep React state in sync with the SDK session (sign-in, sign-out,
    // automatic token refresh, cross-tab changes).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsPremium(false);
      } else if (session?.user) {
        setUser(session.user);
        if (event === 'SIGNED_IN') {
          checkPremiumStatus(session.user.id);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    localStorage.removeItem(LEGACY_KEY);
    setUser(null);
    setIsPremium(false);
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // Ignore errors
    }
  };

  const value = {
    user,
    loading,
    isPremium,
    signOut,
    refreshPremium: () => user && checkPremiumStatus(user.id),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
